import { lazy, Suspense } from 'react';
import { Outlet } from 'react-router-dom';
import { AuthData } from '@/app/auth/contexts/providers/AuthContext';
import { SessionExpirationModal } from '@/app/auth/components/SessionExpirationModal';
import { useAuthorizationError } from '@/app/auth/hooks/useAuthorizationError';

// Lazy load Login component
const Login = lazy(() => import('@/app/auth/logins/pages/Login'));

// Loading component for auth
const AuthLoadingSpinner = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="flex flex-col items-center gap-4">
      <div className="w-12 h-12 border-4 border-primary-color/20 border-t-primary-color rounded-full animate-spin"></div>
      <p className="text-muted-foreground font-medium">Authenticating...</p>
    </div>
  </div>
);

// Loading component for layout/home
const LayoutLoadingSpinner = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="flex flex-col items-center gap-4">
      <div className="w-12 h-12 border-4 border-primary-color/20 border-t-primary-color rounded-full animate-spin"></div>
      <p className="text-muted-foreground font-medium">Loading application...</p>
    </div>
  </div>
);

const Layout = () => {
  // Hook to handle authorization errors (403)
  useAuthorizationError();

  // loginAccount contains user, roles, and token
  const {
    loginAccount: { user, token },
  } = AuthData();

  // Check authentication - DevAuthProvider provides mock data in development
  const isAuthenticated = user && token;

  return (
    <main className="font-niva-book">
      {/* Session expiration modal - always rendered but only visible when session expires */}
      <SessionExpirationModal />

      <Suspense fallback={<AuthLoadingSpinner />}>
        {isAuthenticated ? (
          <Suspense fallback={<LayoutLoadingSpinner />}>
            <Outlet />
          </Suspense>
        ) : (
          <Login />
        )}
      </Suspense>
    </main>
  );
};

export default Layout;
